import { QueryClient } from '@tanstack/react-query';

import { clientApi } from './axios';
import { ReactQueryBuilder } from '../lib/react-query';

const MAX_RETRIES = 2;

/**
 * Retry the failures a retry can actually fix.
 *
 * A 4xx is the server's considered answer: not permitted, not found, not
 * valid. Asking again 300ms later produces the same answer, so the only
 * effects are wasted requests and a console the real error hides in.
 *
 * This is not hypothetical. With the previous flat `retry: 2`, alongside
 * `refetchOnWindowFocus` and a 10 second `staleTime`, a user missing one
 * permission sent three 403s to production every time they focused the tab,
 * for as long as it stayed open. Found when an org owner's clinic had an
 * Admin role predating the flowsheet permissions.
 *
 * 5xx and transport failures (offline, DNS, reset) still retry: those are
 * transient, and they are what the retry is for.
 *
 * Deliberately total. It runs inside the query retryer, so throwing on an
 * unexpected error shape would turn a failed request into an unhandled one.
 */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null | undefined)?.response?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return failureCount < MAX_RETRIES;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 60000, // 1 min
      retryDelay: 300,
      staleTime: 10000, // 10 sec
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      retry: retryUnlessClientError,
    },
  },
});

export const { usePost, useFetch, usePatch, useDelete, useUpdate, useLoadMore, usePrefetch } = new ReactQueryBuilder(
  clientApi,
);
